'use client';

import { Button, Card, CardContent, Input, Logo } from '@lectura/ui';
import { Lock, Mail, User } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useAuth } from '@/lib/auth';

const PASSWORD_MIN = 12; // identity-svc RegisterRequest min_length

/**
 * Regisztráció-oldal — az identity-svc `/auth/register` végpontjával dolgozik.
 *
 * Folyamat:
 * 1. Form-validáció kliens-oldalon (email, display_name, jelszó-egyezés, ≥12 char).
 * 2. POST /api/identity/auth/register — alap "reader" szerepkörrel.
 * 3. Sikeres regisztráció után **automatikus belépés** a `useAuth.login()`-en
 *    keresztül, hogy a felhasználó a következő kattintásra már bejelentkezve
 *    érkezzen a könyvtárba (és a haladás-mentés azonnal működjön).
 */
export default function SignupPage() {
  const router = useRouter();
  const login = useAuth((s) => s.login);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('A két jelszó nem egyezik.');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      setError(`A jelszó legalább ${PASSWORD_MIN} karakter legyen.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/identity/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': 'default' },
        body: JSON.stringify({ email, display_name: displayName, password, roles: ['reader'] }),
      });
      if (!res.ok) {
        const text = await res.text();
        // Próbáljuk kihúzni a Pydantic-üzenetet érthetőbb formára
        try {
          const obj = JSON.parse(text);
          const detail = (obj.errors?.[0]?.message as string | undefined) ?? obj.detail ?? obj.title;
          setError(detail ? `Regisztráció sikertelen: ${detail}` : `Regisztráció sikertelen (${res.status}).`);
        } catch {
          setError(`Regisztráció sikertelen (${res.status}): ${text.slice(0, 200)}`);
        }
        return;
      }
      // Auto-login a frissen létrehozott fiókkal. Új user, nincs MFA-secretje,
      // így a `mfa_required` ág sosem fut. Ha valami miatt mégis, navigálunk
      // a TOTP-input page-re.
      const result = await login(email, password);
      if (result.status === 'mfa_required') {
        router.push(`/mfa/verify?challenge=${encodeURIComponent(result.mfaChallengeToken)}`);
        return;
      }
      router.push('/library');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'A kiszolgáló nem érhető el.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-brand-gradient flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white">
        <CardContent className="p-8">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <h1 className="mb-1 text-center font-display text-2xl font-bold">Hozz létre fiókot</h1>
          <p className="mb-6 text-center text-sm text-[var(--color-foreground-muted)]">
            Néhány másodperc, és máris olvashatsz — a haladásod automatikusan szinkronizálódik.
          </p>
          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <Input
              type="text"
              autoComplete="name"
              required
              minLength={1}
              maxLength={120}
              placeholder="Neved"
              leftSlot={<User className="size-4" />}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Input
              type="email"
              autoComplete="email"
              required
              placeholder="email@példa.com"
              leftSlot={<Mail className="size-4" />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN}
              placeholder={`Jelszó (min. ${PASSWORD_MIN} karakter)`}
              leftSlot={<Lock className="size-4" />}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={PASSWORD_MIN}
              placeholder="Jelszó még egyszer"
              leftSlot={<Lock className="size-4" />}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : null}
            <Button type="submit" loading={submitting} size="lg" className="mt-2">
              Regisztráció
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-[var(--color-foreground-muted)]">
            Van már fiókod?{' '}
            <Link href="/login" className="text-brand-600 hover:underline">
              Jelentkezz be
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
